import { Link } from "@tanstack/react-router";
import { ArrowRight, Phone, CheckCircle2 } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { HeroBackground } from "@/components/HeroBackground";
import { Button } from "@/components/ui/button";
import { company } from "@/data/site";
import { getServiceHeroImage } from "@/data/serviceHeroImages";
import { ServiceTile } from "@/components/ServiceTile";
import { roofingTiles, solarTiles } from "@/data/serviceTiles";
import { RoofingMaterialsScroll } from "@/components/RoofingMaterialsScroll";
import g1 from "@/assets/gallery-new/6940e3a896e7535d5af6346b-2.webp.asset.json";
import g2 from "@/assets/gallery-new/6940e3a896e753e8e1f6346a-2.webp.asset.json";
import g3 from "@/assets/gallery-new/6940e3a8ab25944693304115-2.webp.asset.json";
import g4 from "@/assets/gallery-new/6940e3a8ab25949211304117-2.webp.asset.json";
import g5 from "@/assets/gallery-new/6940e3a8b4f42026887ca890-2.webp.asset.json";
import g6 from "@/assets/gallery-new/6940e3a8b4f420921f7ca893-2.webp.asset.json";
import g7 from "@/assets/gallery-new/6940e3a8ca729802452657d2-2.webp.asset.json";
import g8 from "@/assets/gallery-new/6940e3a997c0231b045981a5-2.webp.asset.json";
import g9 from "@/assets/gallery-new/6940e3a997c0238f425981a9-2.webp.asset.json";
import mRoofRepair from "@/assets/mckay/roof-repair.webp.asset.json";
import mRoofReplacement from "@/assets/mckay/roof-replacement.webp.asset.json";
import mRoofingService from "@/assets/mckay/roofing-service.webp.asset.json";
import mSolarEnergy from "@/assets/mckay/solar-energy.webp.asset.json";
import mSolarInstall from "@/assets/mckay/solar-install.webp.asset.json";

const roofingGallery = [
  mRoofRepair.url,
  g1.url,
  mRoofReplacement.url,
  g3.url,
  mRoofingService.url,
  g5.url,
  g7.url,
  g6.url,
];
const solarGallery = [
  mSolarInstall.url,
  g2.url,
  mSolarEnergy.url,
  g4.url,
  g8.url,
  g9.url,
];


export type RoofingSection = {
  title: string;
  body: string[];
  bullets?: string[];
};

function SectionBlock({
  section,
  image,
  flip,
}: {
  section: RoofingSection;
  image: string;
  flip: boolean;
}) {
  return (
    <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
      <article className={flip ? "lg:order-last" : undefined}>
        <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">{section.title}</h2>
        <div className="mt-5 space-y-4 text-base leading-relaxed text-foreground/85 sm:text-lg">
          {section.body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
          {section.bullets && section.bullets.length > 0 && (
            <ul className="mt-4 space-y-3">
              {section.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-secondary" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </article>
      <div className="overflow-hidden rounded-2xl border border-border bg-muted shadow-elegant">
        <img
          src={image}
          alt={section.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
        />
      </div>
    </div>
  );
}


export type RoofingServiceContent = {
  heroTitle: string;
  heroSubtitle: string;
  intro: string;
  sections: RoofingSection[];
  ctaTitle?: string;
  ctaSubtitle?: string;
  currentSlug: string;
  type?: "roofing" | "solar";
  showMaterials?: boolean;
};

export function RoofingServicePage(props: RoofingServiceContent) {
  const {
    heroTitle,
    heroSubtitle,
    intro,
    sections,
    ctaSubtitle = "Free, no-pressure estimates across Southern California. We'll take a look, explain what we find, and give you clear options.",
    currentSlug,
    type = "roofing",
    showMaterials = false,
  } = props;

  const pool = type === "solar" ? solarTiles : roofingTiles;
  const related = pool.filter((s) => s.slug !== currentSlug).slice(0, 4);
  const otherServicesHref = type === "solar" ? "/services/solar" : "/services/roofing";
  const otherServicesLabel = type === "solar" ? "View Solar Services" : "View Roofing Services";
  const relatedHeading = type === "solar" ? "Other Solar Services" : "Other Roofing Services";
  const relatedSubhead =
    type === "solar"
      ? "Explore the rest of our solar work across Southern California."
      : "Explore the rest of our roofing work across Southern California.";
  const ctaHeadingDefault = type === "solar" ? "Talk to a Local Solar Team" : "Talk to a Local Roofer";
  const resolvedCtaTitle = props.ctaTitle ?? ctaHeadingDefault;
  const heroImage = getServiceHeroImage(currentSlug);
  const showBooking =
    type === "solar" && /repair|maintenance|monitoring|inverter|troubleshoot/i.test(currentSlug);

  const gallery = type === "solar" ? solarGallery : roofingGallery;
  const firstHalf = sections.slice(0, Math.ceil(sections.length / 2));
  const secondHalf = sections.slice(Math.ceil(sections.length / 2));



  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero – uses the matching service card image */}
        <section className="relative min-h-[85vh] w-full overflow-hidden bg-primary text-primary-foreground">
          {heroImage ? (
            <div
              key={heroImage}
              className="absolute inset-0 animate-[heroZoom_1200ms_ease-out_both]"
              style={{
                viewTransitionName: `service-hero-${currentSlug}`,
              } as React.CSSProperties}
            >
              <HeroBackground src={heroImage} alt={heroTitle} overlay="black-soft" />
            </div>
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/85" />
              <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_20%_20%,hsl(var(--secondary)/0.35),transparent_45%),radial-gradient(circle_at_80%_60%,hsl(var(--primary-foreground)/0.15),transparent_50%)]" />
            </>
          )}
          <div className="relative mx-auto flex min-h-[85vh] w-full max-w-7xl items-center px-4 py-24 sm:px-6 lg:px-8 animate-fade-in">
            <div className="max-w-3xl">
              <h1 className="text-4xl font-bold leading-[1.1] sm:text-5xl lg:text-6xl text-balance">
                {heroTitle}
              </h1>
              <p className="mt-6 max-w-2xl text-base text-primary-foreground/85 sm:text-lg">
                {heroSubtitle}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-elegant">
                  <Link to="/contact">
                    Get My Free Estimate <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                  <Link to="/contact">
                    <Phone className="mr-2 h-4 w-4" />
                    {company.phone}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="bg-background">
          <div className="mx-auto max-w-4xl px-4 py-14 text-center sm:px-6 sm:py-16 lg:px-8">
            <p className="text-lg leading-relaxed text-foreground/85 sm:text-xl">{intro}</p>
          </div>
        </section>

        {/* Body sections */}
        <section className="bg-background overflow-x-clip">
          <div className="mx-auto max-w-6xl space-y-16 px-4 pb-16 sm:px-6 sm:pb-20 lg:px-8">
            {firstHalf.map((s, i) => (
              <SectionBlock key={s.title} section={s} image={gallery[i % gallery.length]} flip={i % 2 === 1} />
            ))}

            {/* Middle CTA */}
            <article className="relative left-1/2 w-screen max-w-[100vw] -translate-x-1/2 bg-primary px-6 py-12 text-primary-foreground sm:px-10">
              <div className="mx-auto flex max-w-3xl flex-col items-center gap-5 text-center">
                <div className="text-sm font-bold uppercase tracking-wider text-primary-foreground/70">
                  {showBooking ? "Solar Repair Booking" : type === "solar" ? "Free Solar Consultation" : "Free Roof Inspection"}
                </div>
                <h2 className="text-3xl font-bold sm:text-4xl text-balance">
                  {showBooking
                    ? "System not producing? Book a repair visit online"
                    : type === "solar"
                      ? "Want to know what solar would actually save you?"
                      : "Not sure what your roof really needs?"}
                </h2>
                <p className="max-w-2xl text-base text-primary-foreground/80">
                  {showBooking
                    ? "Pick a day and time that works for you and one of our solar technicians will diagnose the issue on site - no phone tag required."
                    : "Book a free, no-pressure visit. We look at what you have, explain the options in plain English and give you honest numbers before anything moves forward."}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {showBooking ? (
                    <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                      <Link to="/book/solar-repair">Book a Solar Repair <ArrowRight className="ml-1 h-4 w-4" /></Link>
                    </Button>
                  ) : (
                    <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                      <Link to="/contact">Get a Free Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
                    </Button>
                  )}
                  <Button asChild size="lg" className="bg-white/10 text-primary-foreground hover:bg-white/20">
                    <Link to="/contact">
                      <Phone className="mr-2 h-4 w-4" />
                      {company.phone}
                    </Link>
                  </Button>
                </div>
              </div>
            </article>


            {secondHalf.map((s, i) => {
              const idx = firstHalf.length + i;
              return (
                <SectionBlock key={s.title} section={s} image={gallery[idx % gallery.length]} flip={idx % 2 === 1} />
              );
            })}
          </div>
        </section>




        {/* Materials we install */}
        {showMaterials ? <RoofingMaterialsScroll heading="Materials We Install" /> : null}

        {/* Related services */}
        <section className="bg-muted/40">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
            <h2 className="text-2xl font-bold text-primary sm:text-3xl">{relatedHeading}</h2>
            <p className="mt-2 text-muted-foreground">{relatedSubhead}</p>
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {related.map((s) => (
                <ServiceTile key={s.slug} item={s} />
              ))}
              <Link
                to={otherServicesHref}
                className="group relative flex aspect-[3/4] w-full shrink-0 flex-col items-center justify-center overflow-hidden rounded-2xl bg-primary text-center text-primary-foreground shadow-lg ring-1 ring-white/10 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_30%_30%,hsl(var(--secondary)/0.5),transparent_45%),radial-gradient(circle_at_70%_70%,hsl(var(--primary-foreground)/0.25),transparent_50%)]" />
                <div className="relative z-10 flex flex-col items-center gap-3 px-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 backdrop-blur">
                    <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                  <span className="font-display text-lg font-semibold leading-tight sm:text-xl lg:text-2xl">
                    {otherServicesLabel}
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </section>


        {/* CTA */}
        <section className="bg-background">
          <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">{resolvedCtaTitle}</h2>
            <p className="mx-auto mt-4 max-w-2xl text-foreground/80">{ctaSubtitle}</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Button asChild size="lg" className="btn-glow bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow">
                <Link to="/contact">Get My Free Estimate <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact">
                  <Phone className="mr-2 h-4 w-4" />
                  {company.phone}
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}